import React, { useEffect, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from '../../../../../../_metronic/_helpers';
import { injectIntl } from 'react-intl';
import { Pagination } from '../../../../../../_metronic/_partials/controls';
import * as uiHelpers from './../OrdersUIHelpers';
import * as columnFormatters from './column-formatters/';
import { useOrdersUIContext } from './../OrdersUIContext';
import * as actions from '../../../_redux/orders/ordersActions';

const OrdersTable = (props) => {
  const { intl } = props;
  const orderCodeLabel = intl.formatMessage({ id: 'ORDER.LABEL.ORDERCODE' });
  const orderDateLabel = intl.formatMessage({ id: 'ORDER.LABEL.ORDERDATE' });
  const websiteLabel = intl.formatMessage({ id: 'ORDER.LABEL.WEBSITE' });
  const purchaseOrderIdLabel = intl.formatMessage({
    id: 'ORDER.LABEL.PURCHASEORDERID',
  });

  const cardChargeAmountLabel = intl.formatMessage({
    id: 'ORDER.LABEL.CARDCHARGEAMOUNT',
  });
  const statusLabel = intl.formatMessage({
    id: 'ORDER.LABEL.STATUS',
  });
  const actionsLabel = intl.formatMessage({ id: 'ORDER.LABEL.ACTIONS' });

  // Orders UI Context
  const OrdersUIContext = useOrdersUIContext();
  const OrdersUIProps = useMemo(() => {
    return {
      queryParams: OrdersUIContext.queryParams,
      setQueryParams: OrdersUIContext.setQueryParams,
      openViewOrderDialog: OrdersUIContext.openViewOrderDialog,
      openDeleteOrderDialog: OrdersUIContext.openDeleteOrderDialog,
    };
  }, [OrdersUIContext]);

  // Getting curret state of Orders list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.orders }),
    shallowEqual
  );
  //console.log('currentState:', currentState);
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };

  // Orders Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    // server call by queryParams
    dispatch(actions.fetchOrders(OrdersUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OrdersUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: 'id',
      text: orderCodeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'orderDate',
      text: orderDateLabel,
      sort: true,
      //format: "YYYY-MM-DD",
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.DateColumnFormatter,
    },
    {
      dataField: 'websiteCode',
      text: websiteLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'purchaseOrderId',
      text: purchaseOrderIdLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'cardChargeAmount',
      text: cardChargeAmountLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.PriceColumnFormatter,
    },
    {
      dataField: 'status',
      text: statusLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
    },
    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openViewOrderDialog: OrdersUIProps.openViewOrderDialog,
        openDeleteOrderDialog: OrdersUIProps.openDeleteOrderDialog,
      },
      classes: 'text-right pr-0',
      headerClasses: 'text-right pr-3',
      style: {
        minWidth: '100px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: OrdersUIProps.queryParams.pageSize,
    page: OrdersUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bordered={true}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  OrdersUIProps.setQueryParams
                )}
                useSortBy
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
};

export default injectIntl(OrdersTable);
